/**
 * Phase 3C + 3D + 3E: Op Note Ingestion Panel with Full Reconciliation Workflow
 * 
 * Main UI for post-op documentation:
 * - Paste text, Upload PDF/Image, EHR stub
 * - Evidence detection and snippet storage
 * - Clarification prompts for missing documentation
 * - Surgeon confirmation workflow
 * - Final CPT sequencing with modifiers (Phase 3F)
 * - Progress tracking and billable status
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  FileText, 
  Upload, 
  Image, 
  Database, 
  Save,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  PlayCircle,
  Sparkles,
  Search,
  MessageSquareWarning,
  ShieldCheck,
} from 'lucide-react';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useOpNoteIngestion } from '@/hooks/useOpNoteIngestion';
import { ExportPackagePanel } from './ExportPackagePanel';
import { EpicExportPanel } from './EpicExportPanel';
import { useNoteAnalysis } from '@/hooks/useNoteAnalysis';
import { useCptReconciliation } from '@/hooks/useCptReconciliation';
import { useClarificationWorkflow } from '@/hooks/useClarificationWorkflow';
import { OpNoteViewer } from './OpNoteViewer';
import { EnhancedCptReconciliationTable } from './EnhancedCptReconciliationTable';
import { ClarificationPromptsPanel } from './ClarificationPromptsPanel';
import { SurgeonConfirmationPanel } from './SurgeonConfirmationPanel';
import { ReconciliationProgressBar } from './ReconciliationProgressBar';
import { FinalSequencingPanel } from './FinalSequencingPanel';
import { CptProofMapPanel } from './CptProofMapPanel';
import { AuditTimelinePanel, ComplianceStatusCard, OverrideGovernancePanel } from '@/components/compliance';
import { buildDemoAuditTimeline } from '@/lib/compliance/auditTimelineBuilder';
import { evaluateCaseCompliance } from '@/lib/compliance/complianceEngine';
import { generateCaseAuditPdf } from '@/lib/compliance/caseAuditPdfGenerator';
import type { AuditTimelineEvent, ComplianceOverride } from '@/lib/compliance/types';
import { DemoOpNoteType } from '@/lib/demo/demoOpNotes';
import { OpNoteSourceType, SurgeonConfirmationV2, DocumentationStatusV2 } from '@/lib/phase3/types';
import type { BillableReconciliationItem, SequencingResult } from '@/lib/phase3/cptSequencingEngine';
import type { FinalCptLine } from '@/hooks/useFinalCptSequencing';
import type { CaseProofMapResult } from '@/lib/proofmap/types';
import { getProofMapsForCpts } from '@/lib/proofmap/orthopedicRules';
import { runCaseProofMapAnalysis } from '@/lib/proofmap/proofMapEngine';
import { toast } from 'sonner';

interface OpNoteIngestionPanelProps {
  caseId: string | null;
  authorizedCpts?: string[];
  cptDescriptions?: Record<string, string>;
  onNoteUploaded?: () => void;
  onRunReconciliation?: () => void;
  onReconciliationComplete?: () => void;
}

export function OpNoteIngestionPanel({
  caseId,
  authorizedCpts = [],
  cptDescriptions = {},
  onNoteUploaded,
  onRunReconciliation,
  onReconciliationComplete,
}: OpNoteIngestionPanelProps) {
  const { isDemoMode } = useDemoMode();
  const [activeTab, setActiveTab] = useState('paste');
  const [pasteText, setPasteText] = useState('');
  const [selectedDemoNote, setSelectedDemoNote] = useState<DemoOpNoteType | ''>('');
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [replaceReason, setReplaceReason] = useState('');
  const [replacementText, setReplacementText] = useState('');
  const [replacementSource, setReplacementSource] = useState<OpNoteSourceType>('PASTE');
  const [showReconciliationTable, setShowReconciliationTable] = useState(false);
  const [computedSequencingResult, setComputedSequencingResult] = useState<SequencingResult | null>(null);
  const [computedFinalLines, setComputedFinalLines] = useState<FinalCptLine[]>([]);
  const [proofMapResult, setProofMapResult] = useState<CaseProofMapResult | null>(null);
  const [overrides, setOverrides] = useState<ComplianceOverride[]>([]);

  const {
    opNote,
    isSaving,
    error,
    hasNote,
    saveNote,
    replaceNote,
    loadDemoNote,
    availableDemoNotes,
    demoNoteType,
  } = useOpNoteIngestion({
    caseId,
    onStateChange: (state) => {
      if (state === 'note_uploaded') {
        onNoteUploaded?.();
      }
    },
  });

  // Note analysis hook
  const {
    isAnalyzing,
    hasAnalyzed,
    analysisResult,
    analyzeAndStore,
    resetAnalysis,
  } = useNoteAnalysis({
    authorizationId: caseId,
    authorizedCpts,
    isDemoMode,
  });

  // CPT reconciliation hook
  const {
    entries: reconciliationEntries,
    summary: reconciliationSummary,
    isLoading: isLoadingReconciliation,
    isInitializing: isInitializingReconciliation,
    needsInitialization,
    initializeEntries,
    confirmCpt,
    confirmAllCpts,
    fetchEntries,
  } = useCptReconciliation({
    authorizationId: caseId,
    authorizedCpts,
    cptDescriptions,
    isDemoMode,
  });

  // Phase 3E: Clarification workflow hook
  const {
    prompts,
    openPrompts,
    resolvedPrompts,
    loading: loadingClarifications,
    generating: generatingPrompts,
    progress,
    cptStates,
    generatePrompts,
    respondToPrompt,
    dismissPrompt,
    autoResolveAll,
    updateCptConfirmation,
    hasOpenPromptsForCpt,
  } = useClarificationWorkflow({
    authorizationId: caseId,
    cptAnalysisResults: analysisResult?.cptResults || [],
    authorizedCpts,
  });

  // Phase 4+ Compliance: Audit timeline, compliance status, overrides
  const auditTimeline = useMemo<AuditTimelineEvent[]>(() => {
    if (!isDemoMode) return [];
    return buildDemoAuditTimeline({
      caseId: caseId || 'demo',
      caseNumber: caseId || 'DEMO-CASE',
      authorizedCpts,
      surgeonName: 'Dr. Michael Larkin, MD',
      hasAttestation: true,
      hasOpNote: hasNote,
      hasAnalysis: hasAnalyzed,
      hasConfirmations: Object.values(cptStates).some(s => s?.surgeonConfirmation === 'confirmed_performed'),
      hasExport: false,
      confirmedCpts: authorizedCpts.filter(cpt => cptStates[cpt]?.surgeonConfirmation === 'confirmed_performed'),
    });
  }, [isDemoMode, caseId, authorizedCpts, hasNote, hasAnalyzed, cptStates]);

  const complianceStatus = useMemo(() => {
    return evaluateCaseCompliance({
      caseId: caseId || 'demo',
      hasSurgeonAttestation: isDemoMode ? true : false,
      authorizedCpts,
      cptStates: Object.fromEntries(
        authorizedCpts.map(cptCode => {
          const state = cptStates[cptCode];
          const ar = analysisResult?.cptResults.find(r => r.cptCode === cptCode);
          return [cptCode, {
            surgeonConfirmation: state?.surgeonConfirmation || null,
            billableStatus: state?.billableStatus || 'review_required',
            documentationStatus: ar?.documentationStatus || undefined,
          }];
        })
      ),
      hasOpNote: hasNote,
      hasAnalysis: hasAnalyzed,
      hasConflicts: false,
      overrideCount: overrides.length,
      exportGatesPass: progress.canExport,
      isDemoMode,
    });
  }, [caseId, isDemoMode, authorizedCpts, cptStates, analysisResult, hasNote, hasAnalyzed, overrides, progress]);

  const handleApplyOverride = useCallback(async (override: {
    overrideType: any; reason: string; cptCode?: string; previousValue: string; newValue: string;
  }): Promise<boolean> => {
    const newOverride: ComplianceOverride = {
      id: `override-${Date.now()}`, caseId: caseId || 'demo', cptCode: override.cptCode,
      overrideType: override.overrideType, reason: override.reason,
      previousValue: override.previousValue, newValue: override.newValue,
      actorId: 'demo-user', actorEmail: isDemoMode ? 'ops@linkhealth.demo' : 'current-user',
      actorRole: 'link_ops', timestamp: new Date().toISOString(), affectsClinicalContent: false,
    };
    setOverrides(prev => [...prev, newOverride]);
    return true;
  }, [caseId, isDemoMode]);

  const handleExportAuditPdf = useCallback(() => {
    const doc = generateCaseAuditPdf({
      caseNumber: caseId || 'DEMO-CASE', caseId: caseId || 'demo',
      authReference: isDemoMode ? 'AUTH-2024-DEMO-001' : undefined,
      dateOfService: isDemoMode ? new Date().toLocaleDateString() : undefined,
      laterality: isDemoMode ? 'Right' : undefined,
      surgeonName: isDemoMode ? 'Dr. Michael Larkin, MD' : undefined,
      facilityName: isDemoMode ? 'Cincinnati Orthopedic Institute' : undefined,
      attestationText: isDemoMode ? 'I attest that the selected procedures represent clinically possible intra-operative findings for this case.' : undefined,
      attestationTimestamp: isDemoMode ? new Date().toISOString() : undefined,
      operativeNoteText: opNote?.rawText,
      cptLines: computedFinalLines.map(l => ({
        sequence: l.sequence, cptCode: l.cptCode, cptDescription: cptDescriptions[l.cptCode],
        modifiers: l.modifiers, units: l.units, finalCodeString: l.finalCodeString,
        modifierExplanations: l.modifierExplanations,
      })),
      authorizedCpts, authorizationScope: 'Standard',
      proofMapSummary: proofMapResult?.cpt_results.map(r => ({
        cptCode: r.cpt_code, documentationStatus: r.documentation_status,
        requirementsSatisfied: r.satisfied_count, requirementsTotal: r.total_requirements,
        evidenceSnippetCount: r.all_evidence_snippets.length,
      })),
      surgeonConfirmations: authorizedCpts.map(cpt => ({
        cptCode: cpt, confirmation: cptStates[cpt]?.surgeonConfirmation || 'pending',
        billableStatus: cptStates[cpt]?.billableStatus || 'review_required',
      })),
      auditTimeline, overrides, complianceStatus,
      isDemoExport: isDemoMode, generatedAt: new Date().toISOString(),
      generatedBy: isDemoMode ? 'Demo User (LINK Ops)' : undefined,
    });
    doc.save(isDemoMode ? `LINK_DEMO_AUDIT_${caseId || 'case'}.pdf` : `LINK_AUDIT_${caseId || 'case'}.pdf`);
    toast.success('Audit PDF downloaded');
  }, [caseId, isDemoMode, opNote, computedFinalLines, authorizedCpts, cptDescriptions, proofMapResult, cptStates, auditTimeline, overrides, complianceStatus]);

  // Handle running reconciliation (analysis + clarification generation)
  const handleRunReconciliation = useCallback(async () => {
    if (!opNote?.rawText) {
      toast.error('No operative note text available');
      return;
    }

    toast.info('Analyzing operative note...', {
      description: 'Searching for documentation evidence',
    });

    // Initialize reconciliation entries if needed
    if (needsInitialization) {
      await initializeEntries();
    }

    // Run analysis
    const { success, result } = await analyzeAndStore(opNote.rawText);

    if (success && result) {
      const cptsWithEvidence = result.cptResults.filter(r => r.detectedInNote).length;
      const cptsWithSufficient = result.cptResults.filter(r => r.documentationStatus === 'sufficient').length;
      const cptsNeedingClarification = result.cptResults.filter(
        r => r.documentationStatus === 'insufficient' || r.documentationStatus === 'ambiguous'
      ).length;
      
      toast.success('Analysis complete', {
        description: `Found evidence for ${cptsWithEvidence}/${result.totalCptsAnalyzed} CPTs. ${cptsWithSufficient} have sufficient documentation.`,
      });

      // Run CPT Proof Map analysis
      const proofMaps = getProofMapsForCpts(authorizedCpts);
      if (proofMaps.length > 0 && opNote?.rawText) {
        const pmResult = runCaseProofMapAnalysis(
          caseId || 'demo',
          proofMaps,
          [{ text: opNote.rawText, source: 'OP_NOTE' as const }],
        );
        setProofMapResult(pmResult);
      }

      // Refresh reconciliation entries
      await fetchEntries();
      setShowReconciliationTable(true);

      // Generate clarification prompts for CPTs with insufficient/ambiguous docs
      if (cptsNeedingClarification > 0 && !isDemoMode) {
        toast.info(`${cptsNeedingClarification} CPT(s) need clarification`, {
          description: 'Generating clarification prompts...',
        });
        await generatePrompts();
      }

      // In demo mode, auto-resolve clarifications and confirm all CPTs
      if (isDemoMode) {
        setTimeout(async () => {
          await autoResolveAll();
          await confirmAllCpts();
          onReconciliationComplete?.();
        }, 500);
      }

      onRunReconciliation?.();
    } else {
      toast.error('Analysis failed', {
        description: 'Please try again or contact support',
      });
    }
  }, [opNote, needsInitialization, initializeEntries, analyzeAndStore, fetchEntries, isDemoMode, confirmAllCpts, generatePrompts, autoResolveAll, onRunReconciliation, onReconciliationComplete]);

  // Handle surgeon confirmation from the confirmation panel
  const handleSurgeonConfirmation = useCallback(async (
    cptCode: string,
    confirmation: SurgeonConfirmationV2,
    reason?: string
  ): Promise<boolean> => {
    // Update the clarification workflow state
    updateCptConfirmation(cptCode, confirmation);
    
    // Map to reconciliation status
    const statusMap: Record<SurgeonConfirmationV2, 'confirmed_performed' | 'not_performed' | 'insufficient_documentation'> = {
      confirmed_performed: 'confirmed_performed',
      not_performed: 'not_performed',
      not_applicable: 'not_performed',
      deferred: 'insufficient_documentation',
    };
    
    // Update the CPT reconciliation entry
    const success = await confirmCpt(cptCode, statusMap[confirmation], 'direct', reason);
    
    if (success) {
      toast.success(`CPT ${cptCode} ${confirmation === 'confirmed_performed' ? 'confirmed as performed' : 'updated'}`);
    }
    
    return success;
  }, [updateCptConfirmation, confirmCpt]);

  // Handle confirm all as performed (demo mode)
  const handleConfirmAllAsPerformed = useCallback(async (): Promise<boolean> => {
    // Update all CPT states
    for (const cptCode of authorizedCpts) {
      updateCptConfirmation(cptCode, 'confirmed_performed');
    }
    
    // Confirm all via reconciliation hook
    const success = await confirmAllCpts();
    
    if (success) {
      toast.success('All CPTs confirmed as performed');
      onReconciliationComplete?.();
    }
    
    return success;
  }, [authorizedCpts, updateCptConfirmation, confirmAllCpts, onReconciliationComplete]);

  // Auto-run analysis in demo mode after note is loaded
  useEffect(() => {
    if (isDemoMode && hasNote && opNote?.rawText && !hasAnalyzed && !isAnalyzing) {
      handleRunReconciliation();
    }
  }, [isDemoMode, hasNote, opNote?.rawText, hasAnalyzed, isAnalyzing, handleRunReconciliation]);

  // Handle paste submission
  const handlePasteSubmit = useCallback(async () => {
    if (!pasteText.trim()) {
      toast.error('Please paste operative note text');
      return;
    }
    
    const success = await saveNote(pasteText, 'PASTE');
    if (success) {
      toast.success('Operative note saved successfully');
      setPasteText('');
    } else if (error) {
      toast.error(error.message);
    }
  }, [pasteText, saveNote, error]);

  // Handle demo note selection
  const handleDemoNoteSelect = useCallback((noteType: string) => {
    if (noteType && noteType !== '') {
      setSelectedDemoNote(noteType as DemoOpNoteType);
      loadDemoNote(noteType as DemoOpNoteType);
      toast.success('Demo op note loaded');
    }
  }, [loadDemoNote]);

  // Handle file upload (PDF)
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // For demo, just show a message
    if (isDemoMode) {
      toast.info('In demo mode, use the EHR Samples tab for pre-loaded notes');
      return;
    }

    // In production, this would send to an edge function for text extraction
    // For now, show placeholder behavior
    if (file.type === 'application/pdf') {
      toast.info('PDF text extraction requires production integration. Use paste for now.');
    } else if (file.type.startsWith('image/')) {
      toast.info('OCR text extraction requires production integration. Use paste for now.');
    } else {
      // Try to read as text
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        if (text) {
          const success = await saveNote(text, 'UPLOAD_PDF');
          if (success) {
            toast.success('File text saved successfully');
          }
        }
      };
      reader.readAsText(file);
    }
  }, [isDemoMode, saveNote]);

  // Handle note replacement
  const handleReplaceSubmit = useCallback(async () => {
    if (!replacementText.trim() || !replaceReason.trim()) {
      toast.error('Both replacement text and reason are required');
      return;
    }

    const success = await replaceNote(replacementText, replacementSource, replaceReason);
    if (success) {
      toast.success('Operative note replaced successfully');
      setShowReplaceDialog(false);
      setReplaceReason('');
      setReplacementText('');
    } else if (error) {
      toast.error(error.message);
    }
  }, [replacementText, replaceReason, replacementSource, replaceNote, error]);

  // If note already exists, show viewer and reconciliation
  if (hasNote && opNote) {
    return (
      <div className="space-y-6">
        {/* Op Note Viewer */}
        <OpNoteViewer
          rawText={opNote.rawText}
          sourceType={opNote.sourceType}
          uploadedAt={opNote.uploadedAt}
          uploadedBy={opNote.uploadedBy}
          onReplaceNote={() => setShowReplaceDialog(true)}
          canReplace={true}
          isDemoMode={isDemoMode}
        />
        
        {/* Analysis Status or Run Button */}
        {!hasAnalyzed && !isAnalyzing && (
          <Alert className="border-primary/50 bg-primary/5">
            <Search className="h-4 w-4 text-primary" />
            <AlertTitle>Ready for Analysis</AlertTitle>
            <AlertDescription>
              Run reconciliation to detect evidence for each authorized CPT code.
            </AlertDescription>
          </Alert>
        )}

        {isAnalyzing && (
          <Alert className="border-primary/50 bg-primary/5">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <AlertTitle>Analyzing Operative Note...</AlertTitle>
            <AlertDescription>
              Searching for documentation evidence for {authorizedCpts.length} CPT codes.
            </AlertDescription>
          </Alert>
        )}

        {/* Run Reconciliation Button - only show if not yet analyzed */}
        {!hasAnalyzed && (
          <div className="flex justify-end">
            <Button 
              onClick={handleRunReconciliation}
              disabled={isAnalyzing}
              className="gap-2"
              size="lg"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <PlayCircle className="h-5 w-5" />
                  Run Reconciliation
                </>
              )}
            </Button>
          </div>
        )}

        {/* CPT Reconciliation Table - show after analysis */}
        {(hasAnalyzed || showReconciliationTable) && (
          <EnhancedCptReconciliationTable
            entries={reconciliationEntries}
            summary={reconciliationSummary}
            isLoading={isLoadingReconciliation}
            isInitializing={isInitializingReconciliation}
            needsInitialization={needsInitialization}
            onInitialize={initializeEntries}
            onConfirmCpt={confirmCpt}
            onConfirmAll={confirmAllCpts}
            cptAnalysisResults={analysisResult?.cptResults}
            fullNoteText={opNote.rawText}
            hasAnalyzed={hasAnalyzed}
            cptDescriptions={cptDescriptions}
          />
        )}

        {/* CPT Proof Map Panel - show after analysis */}
        {hasAnalyzed && proofMapResult && (
          <CptProofMapPanel
            proofMapResult={proofMapResult}
            isAnalyzing={isAnalyzing}
            isDemoMode={isDemoMode}
          />
        )}

        {/* Phase 3E: Reconciliation Progress Bar */}
        {hasAnalyzed && (
          <ReconciliationProgressBar progress={progress} showDetails={true} />
        )}

        {/* Phase 3E: Clarification Prompts Panel - show if there are any prompts */}
        {hasAnalyzed && prompts.length > 0 && (
          <ClarificationPromptsPanel
            prompts={prompts}
            openPrompts={openPrompts}
            resolvedPrompts={resolvedPrompts}
            isLoading={loadingClarifications}
            onRespond={respondToPrompt}
            onDismiss={dismissPrompt}
            onAutoResolveAll={isDemoMode ? autoResolveAll : undefined}
          />
        )}

        {/* Phase 3E: Surgeon Confirmation Panel */}
        {hasAnalyzed && (
          <SurgeonConfirmationPanel
            cptRows={authorizedCpts.map(cptCode => {
              const analysisResult_cpt = analysisResult?.cptResults.find(r => r.cptCode === cptCode);
              const cptState = cptStates[cptCode];
              return {
                cptCode,
                cptDescription: cptDescriptions[cptCode],
                documentationStatus: (analysisResult_cpt?.documentationStatus || 'not_found') as DocumentationStatusV2,
                hasPendingClarifications: hasOpenPromptsForCpt(cptCode),
                surgeonConfirmation: cptState?.surgeonConfirmation || null,
                billableStatus: cptState?.billableStatus || 'review_required',
                billableReason: cptState?.billableReason || 'Pending confirmation',
              };
            })}
            progress={progress}
            isLoading={isLoadingReconciliation}
            onConfirm={handleSurgeonConfirmation}
            onConfirmAllAsPerformed={isDemoMode ? handleConfirmAllAsPerformed : undefined}
            canProceedToExport={progress.canExport}
          />
        )}

        {/* Phase 3F: Final CPT Sequencing Panel */}
        {hasAnalyzed && progress.canExport && (
          <FinalSequencingPanel
            authorizationId={caseId}
            billableItems={authorizedCpts
              .filter(cptCode => {
                const cptState = cptStates[cptCode];
                return cptState?.surgeonConfirmation === 'confirmed_performed' &&
                       cptState?.billableStatus === 'billable';
              })
              .map(cptCode => {
                const analysisResult_cpt = analysisResult?.cptResults.find(r => r.cptCode === cptCode);
                return {
                  id: `${caseId}-${cptCode}`,
                  cptCode,
                  cptDescription: cptDescriptions[cptCode],
                  evidenceSnippets: analysisResult_cpt?.allEvidenceSnippets || [],
                  documentationText: opNote?.rawText,
                  laterality: null, // Would come from case data
                  siteOfService: 'hospital' as const, // Would come from case data
                } as BillableReconciliationItem;
              })}
            siteOfService="hospital"
            onExport={() => {
              // Export handled by ExportPackagePanel below
            }}
            onSequencingComplete={(lines, result) => {
              setComputedFinalLines(lines);
              setComputedSequencingResult(result);
            }}
            userRole="link_ops"
          />
        )}

        {/* Phase 3G: Export Package Panel */}
        {hasAnalyzed && progress.canExport && (
          <ExportPackagePanel
            caseId={caseId}
            caseNumber={caseId || 'DEMO-CASE'}
            attestationText={isDemoMode 
              ? 'I attest that the selected procedures represent clinically possible intra-operative findings for this case.'
              : undefined
            }
            attestationTimestamp={isDemoMode ? new Date().toISOString() : undefined}
            finalLines={computedFinalLines.length > 0 ? computedFinalLines : authorizedCpts
              .filter(cptCode => {
                const cptState = cptStates[cptCode];
                return cptState?.surgeonConfirmation === 'confirmed_performed' &&
                       cptState?.billableStatus === 'billable';
              })
              .map((cptCode, index) => ({
                id: `seq-${caseId}-${cptCode}-${index}`,
                authorizationId: caseId || '',
                cptCode,
                sequence: index + 1,
                modifiers: [],
                units: 1,
                finalCodeString: cptCode,
                baseReimbursement: 0,
                effectiveReimbursement: 0,
                modifierExplanations: {},
                isPrimary: index === 0,
                reviewRequired: false,
                reviewReasons: [],
                isOverridden: false,
                isDemoEntry: isDemoMode,
              }))}
            sequencingResult={computedSequencingResult}
            cptStates={Object.fromEntries(
              authorizedCpts.map(cptCode => {
                const state = cptStates[cptCode];
                const analysisResult_cpt = analysisResult?.cptResults.find(r => r.cptCode === cptCode);
                return [cptCode, {
                  surgeonConfirmation: state?.surgeonConfirmation || null,
                  billableStatus: state?.billableStatus || 'review_required',
                  documentationStatus: analysisResult_cpt?.documentationStatus || 'not_found',
                }];
              })
            )}
            authorizedCpts={authorizedCpts}
            cptDescriptions={cptDescriptions}
            hasOpenPromptsForCpt={hasOpenPromptsForCpt}
            analysisComplete={hasAnalyzed}
            postOpState={isDemoMode ? 'export_ready' : undefined}
            userRole="link_ops"
          />
        )}

        {/* Phase 4: Epic-Ready Export Panel */}
        {hasAnalyzed && progress.canExport && (
          <EpicExportPanel
            caseId={caseId}
            caseNumber={caseId || 'DEMO-CASE'}
            authReference={isDemoMode ? 'AUTH-2024-DEMO-001' : undefined}
            dateOfService={isDemoMode ? new Date().toLocaleDateString() : undefined}
            laterality={isDemoMode ? 'Right' : undefined}
            surgeonName={isDemoMode ? 'Dr. Michael Larkin, MD' : undefined}
            facilityName={isDemoMode ? 'Cincinnati Orthopedic Institute' : undefined}
            attestationText={isDemoMode 
              ? 'I attest this operative note accurately reflects the care provided.'
              : undefined
            }
            attestationTimestamp={isDemoMode ? new Date().toISOString() : undefined}
            operativeNoteText={opNote?.rawText}
            finalLines={computedFinalLines.length > 0 ? computedFinalLines : authorizedCpts
              .filter(cptCode => {
                const cptState = cptStates[cptCode];
                return cptState?.surgeonConfirmation === 'confirmed_performed' &&
                       cptState?.billableStatus === 'billable';
              })
              .map((cptCode, index) => ({
                id: `seq-${caseId}-${cptCode}-${index}`,
                authorizationId: caseId || '',
                cptCode,
                sequence: index + 1,
                modifiers: [],
                units: 1,
                finalCodeString: cptCode,
                baseReimbursement: 0,
                effectiveReimbursement: 0,
                modifierExplanations: {},
                isPrimary: index === 0,
                reviewRequired: false,
                reviewReasons: [],
                isOverridden: false,
                isDemoEntry: isDemoMode,
              }))}
            authorizedCpts={authorizedCpts}
            cptDescriptions={cptDescriptions}
            cptStates={Object.fromEntries(
              authorizedCpts.map(cptCode => {
                const state = cptStates[cptCode];
                const analysisResult_cpt = analysisResult?.cptResults.find(r => r.cptCode === cptCode);
                return [cptCode, {
                  surgeonConfirmation: state?.surgeonConfirmation || null,
                  billableStatus: state?.billableStatus || 'review_required',
                  documentationStatus: analysisResult_cpt?.documentationStatus || 'not_found',
                }];
              })
            )}
            proofMapSummary={proofMapResult?.cpt_results.map(r => ({
              cptCode: r.cpt_code,
              documentationStatus: r.documentation_status,
              requirementsSatisfied: r.satisfied_count,
              requirementsTotal: r.total_requirements,
              evidenceSnippetCount: r.all_evidence_snippets.length,
            }))}
            auditTimeline={auditTimeline.map(e => ({ timestamp: e.timestamp, action: e.description, actor: e.actor, details: e.details ? JSON.stringify(e.details) : undefined }))}
            canExport={true}
            gatesPassed={authorizedCpts.length}
            gatesTotal={authorizedCpts.length}
            postOpState={isDemoMode ? 'export_ready' : undefined}
            userRole="link_ops"
          />
        )}

        {/* Phase 4+: Compliance Status & Audit Timeline */}
        {hasAnalyzed && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ComplianceStatusCard
              compliance={complianceStatus}
              isDemoMode={isDemoMode}
            />
            <div className="lg:col-span-2">
              <AuditTimelinePanel
                events={auditTimeline}
                caseNumber={caseId || 'DEMO-CASE'}
                isDemoMode={isDemoMode}
                onExportPdf={handleExportAuditPdf}
                compact={false}
              />
            </div>
          </div>
        )}

        {/* Phase 4+: Override Governance */}
        {hasAnalyzed && (
          <OverrideGovernancePanel
            caseId={caseId || 'demo'}
            overrides={overrides}
            userRole="link_ops"
            userEmail={isDemoMode ? 'ops@linkhealth.demo' : undefined}
            isDemoMode={isDemoMode}
            onApplyOverride={handleApplyOverride}
          />
        )}

        {/* Analysis Summary - shown after analysis */}
        {hasAnalyzed && analysisResult && (
          <Card className="border-primary/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                Analysis Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-2xl font-bold text-foreground">
                    {analysisResult.totalCptsAnalyzed}
                  </p>
                  <p className="text-xs text-muted-foreground">CPTs Analyzed</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-2xl font-bold text-success">
                    {analysisResult.cptsWithEvidence}
                  </p>
                  <p className="text-xs text-muted-foreground">With Evidence</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-2xl font-bold text-primary">
                    {analysisResult.cptsWithSufficientDocs}
                  </p>
                  <p className="text-xs text-muted-foreground">Sufficient Docs</p>
                </div>
              </div>
              
              {isDemoMode && (
                <div className="mt-4 p-3 bg-warning/10 border border-warning/30 rounded-lg flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-warning" />
                  <span className="text-sm text-warning">
                    Demo Mode: All CPTs auto-confirmed as performed
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Replace Note Dialog */}
        <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-warning" />
                Replace Operative Note
              </DialogTitle>
              <DialogDescription>
                This action will be logged with full audit trail. The original note will be preserved in the activity log.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="replace-reason">Reason for Replacement *</Label>
                <Input
                  id="replace-reason"
                  placeholder="e.g., Corrected operative note from EHR, transcription error fixed"
                  value={replaceReason}
                  onChange={(e) => setReplaceReason(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="replace-source">Source Type</Label>
                <Select 
                  value={replacementSource} 
                  onValueChange={(v) => setReplacementSource(v as OpNoteSourceType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PASTE">Pasted Text</SelectItem>
                    <SelectItem value="UPLOAD_PDF">Uploaded PDF</SelectItem>
                    <SelectItem value="UPLOAD_IMAGE">Uploaded Image</SelectItem>
                    <SelectItem value="EHR_STUB">EHR Import</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="replace-text">New Operative Note Text *</Label>
                <Textarea
                  id="replace-text"
                  placeholder="Paste the corrected operative note text here..."
                  value={replacementText}
                  onChange={(e) => setReplacementText(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReplaceDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReplaceSubmit}
                disabled={!replacementText.trim() || !replaceReason.trim() || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Replacing...
                  </>
                ) : (
                  'Replace Note'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Note input UI
  return (
    <Card className="border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Post-Op Documentation Input
            </CardTitle>
            <CardDescription className="mt-1">
              Upload or paste the operative note for reconciliation with authorized procedures
            </CardDescription>
          </div>
          {isDemoMode && (
            <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
              Demo Mode — No Data Saved
            </Badge>
          )}
        </div>
        
        {authorizedCpts.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-sm text-muted-foreground">Authorized CPTs:</span>
            {authorizedCpts.map(cpt => (
              <Badge key={cpt} variant="secondary">{cpt}</Badge>
            ))}
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="paste" className="gap-1">
              <FileText className="h-4 w-4" />
              Paste
            </TabsTrigger>
            <TabsTrigger value="pdf" className="gap-1">
              <Upload className="h-4 w-4" />
              PDF
            </TabsTrigger>
            <TabsTrigger value="image" className="gap-1">
              <Image className="h-4 w-4" />
              Image
            </TabsTrigger>
            <TabsTrigger value="ehr" className="gap-1">
              <Database className="h-4 w-4" />
              {isDemoMode ? 'EHR Samples' : 'EHR'}
            </TabsTrigger>
          </TabsList>
          
          {/* Paste Tab */}
          <TabsContent value="paste" className="mt-4 space-y-4">
            <Textarea
              placeholder="Paste the complete operative note text here..."
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="min-h-[300px] font-mono text-sm"
            />
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {pasteText.length} characters
              </span>
              <Button 
                onClick={handlePasteSubmit}
                disabled={!pasteText.trim() || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Operative Note
                  </>
                )}
              </Button>
            </div>
          </TabsContent>
          
          {/* PDF Upload Tab */}
          <TabsContent value="pdf" className="mt-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Upload PDF Document</p>
              <p className="text-sm text-muted-foreground mb-4">
                Text will be extracted automatically
              </p>
              <input
                type="file"
                accept=".pdf,.txt,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
                id="pdf-upload"
              />
              <label htmlFor="pdf-upload">
                <Button asChild>
                  <span>Choose File</span>
                </Button>
              </label>
              {isDemoMode && (
                <p className="mt-4 text-sm text-muted-foreground">
                  In demo mode, use the EHR Samples tab for pre-loaded examples
                </p>
              )}
            </div>
          </TabsContent>
          
          {/* Image Upload Tab */}
          <TabsContent value="image" className="mt-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Upload Image for OCR</p>
              <p className="text-sm text-muted-foreground mb-4">
                Supports JPG, PNG, HEIC images of typed or printed notes
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload">
                <Button asChild variant="outline">
                  <span>Choose Image</span>
                </Button>
              </label>
              <p className="mt-4 text-sm text-muted-foreground">
                OCR extraction requires production integration
              </p>
            </div>
          </TabsContent>
          
          {/* EHR / Demo Samples Tab */}
          <TabsContent value="ehr" className="mt-4">
            {isDemoMode ? (
              <div className="space-y-4">
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-medium text-primary">
                      Demo Op Note Library
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Select a sample orthopedic operative note to demonstrate the reconciliation workflow.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Select Demo Op Note</Label>
                  <Select
                    value={selectedDemoNote}
                    onValueChange={handleDemoNoteSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an operative note example..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDemoNotes.map(note => (
                        <SelectItem key={note.type} value={note.type}>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-xs">
                              {note.cpt}
                            </Badge>
                            <span>{note.title}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {demoNoteType && (
                  <div className="p-3 bg-primary/10 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span className="text-sm">
                      Demo note loaded. Reconciliation will run automatically.
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">EHR Integration</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Import operative notes directly from Epic or other EHR systems
                </p>
                <Button variant="outline" disabled>
                  Connect EHR (Coming Soon)
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        {error && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">{error.message}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
